import React, { createContext, useContext } from "react";

import { DriverGetQueuePositionData, SettingsCompanyApi, useDriverGetQueuePosition } from "@/domain";
import { useSession } from "@/hooks/@custom/useSession";
import { useGetIpQuery } from "@/store/apis/ipify";
import { useGetCheckinsCompanyActivesByCompanyQuery, useGetSettingsByCompanyQuery } from "@/store/apis/nextcargo";

import { useLoader } from "./loader";

type SettingsCompany = SettingsCompanyApi;

interface DriverCheckIn {
  id: number;
  driverQueueId: number;
  driverCheckInActive: boolean;
  checkInCreatedAt: string;
}

interface DriverContextData {
  isDriverIPAuthorized: boolean | null;
  settingsCompany?: SettingsCompany;
  driverCheckIn: DriverCheckIn | null;
  driverId?: string | null;
  driverCompanyId?: string;
  driverCompanyName: string | undefined;
  driverFirstName: string | undefined;
  isLoading: boolean;
  fetchCheckInActive: () => void;
  updateCheckin: (checkin: DriverCheckIn | null) => void;
  driverQueuePosition: DriverGetQueuePositionData;
}

const DriverContext = createContext({} as DriverContextData);

function DriverProvider({ children }: { children: React.ReactNode }) {
  const { driver } = useSession();
  const driverCompanyId = driver?.company.id ? String(driver?.company.id) : undefined;
  const driverId = driver?.id ? String(driver?.id) : undefined;

  const { data: settingsCompany, isLoading: isLoadingSettings } = useGetSettingsByCompanyQuery({ id: Number(driverCompanyId) }, { skip: !driverCompanyId });
  const {
    data: driverCheckIn,
    isLoading: isLoadingCheckins,
    refetch
  } = useGetCheckinsCompanyActivesByCompanyQuery(
    { id: Number(driverCompanyId) },
    {
      skip: !driverCompanyId,
      selectFromResult: ({ data, ...state }) => {
        const driverHasCheckin = data?.original.find((item) => item?.queue?.driver?.id.toString() === driverId);

        return {
          ...state,
          data: driverHasCheckin
            ? {
                id: driverHasCheckin.id,
                driverQueueId: driverHasCheckin?.queue?.id,
                driverCheckInActive: driverHasCheckin.isPresent,
                checkInCreatedAt: driverHasCheckin.updatedAt
              }
            : undefined
        };
      }
    }
  );
  const { data: ipifyData, isLoading: isLoadingIp } = useGetIpQuery();

  const allowedIps = (settingsCompany?.checkinIp ?? "")
    .split(",")
    .map((ip) => ip.trim())
    .filter((ip) => ip.length > 0);
  const isCurrentDriverAllowed = ipifyData?.ip ? allowedIps.includes(ipifyData.ip) : false;

  /* ######################################################################################### */

  const { isLoading } = useLoader();
  const driverQueuePosition = useDriverGetQueuePosition(driverId ?? "");

  const driverName = driver?.name;
  const driverCompanyName = driver?.company.name;
  const driverFirstName = driverName?.split(" ")[0];

  return (
    <DriverContext.Provider
      value={{
        isLoading: isLoading || isLoadingSettings || isLoadingIp || isLoadingCheckins,
        isDriverIPAuthorized: isCurrentDriverAllowed,
        driverCheckIn: {
          id: driverCheckIn?.id ?? 0,
          driverQueueId: driverCheckIn?.driverQueueId ?? 0,
          driverCheckInActive: driverCheckIn?.driverCheckInActive ?? false,
          checkInCreatedAt: driverCheckIn?.checkInCreatedAt ?? ""
        },
        settingsCompany,
        driverId,
        driverCompanyId,
        driverCompanyName,
        driverFirstName,
        fetchCheckInActive: refetch,
        updateCheckin: refetch,
        driverQueuePosition
      }}
    >
      {children}
    </DriverContext.Provider>
  );
}

const useDriver = () => {
  const context = useContext(DriverContext);
  if (!context) {
    throw new Error("useDriver must be used within a DriverProvider");
  }
  return context;
};

export { DriverProvider, useDriver };
